<template>
    <v-card v-if="attributeFormField">
        <v-card-title>
            <span v-if="isNewAttribute"> Add Attribute </span>
            <span v-else> Edit Attribute {{ attributeFormField.name }} </span>
            <span v-if="!isRegistrySchema" class="pl-5">
                <v-chip dark>
                    {{ attributeFormField.source}}
                </v-chip>
            </span>
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="3" v-if="isNewAttribute">
                    <v-text-field label="Name" v-model="attributeFormField.name" />
                </v-col>
                <v-col cols="3">
                    <v-text-field label="Display Name" v-model="attributeFormField.display_name" />
                </v-col>
                <v-col>
                    <v-autocomplete
                        label="Attribute Type"
                        v-model="attributeFormField.type"
                        :items="attributeTypes"
                        @update:modelValue="typeChanged"
                    />
                </v-col>
                <v-col>
                    <v-switch
                        label="Multiple Values?"
                        v-model="attributeFormField.allow_multiple_values"
                    />
                </v-col>
                <v-col>
                    <v-text-field label="Order" v-model="attributeFormField.display_order" />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="2">
                    <v-autocomplete
                        label="Widget"
                        v-model="attributeFormField.widget_type"
                        :items="widgetTypes"
                    />
                </v-col>
                <!-- entry_relation and global_relation - pick the relation -->
                <v-col cols="4" v-if="isRelation">
                    <v-autocomplete
                        label="Relation"
                        v-model="attributeFormField.relation"
                        :items="relationOptions"
                        item-title="displayName"
                        item-value="name"
                        @update:modelValue="fetchRegistryEntriesForGlobalRelation"
                    />
                </v-col>
                <!-- Dropdown widget type options -->
                <v-col cols="4" v-if="isBasicDropdown">
                    <span>Options</span>
                    <v-text-field
                        label="Add option"
                        v-model="newDropdownOptionValue"
                        variant="outlined"
                        density="compact"
                        hide-details
                        @change="addDropdownOption"
                        :type="getDropdownOptionType()"
                    >
                        <template v-slot:append>
                            <v-icon @click="addDropdownOption" color="green"> $plus-circle </v-icon>
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="4" v-if="!isRegistrySchema">
                    <v-autocomplete
                        v-if="isBasicDropdown"
                        label="Default Value"
                        v-model="defaultValue"
                        class="mb-n5 mt-1"
                        :items="dropdownOptions"
                    />
                    <v-autocomplete
                        v-if="isRelationAttribute"
                        label="Default Value"
                        v-model="defaultValue"
                        class="mb-n5 mt-1"
                        :items="defaultRelationValueOptions"
                    />
                    <v-text-field
                        v-if="isBasicDefaultValue"
                        label="Default Value"
                        v-model="defaultValue"
                        :type="attributeFormField.type === 'number' ? 'number' : 'text'"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" v-if="isBasicDropdown">
                    <v-data-table :items="dropdownTableItems" :headers="dropdownOptionHeaders" items-per-page="8">
                        <template v-slot:item.delete="{ item }">
                            <v-icon-btn @click="removeDropdownOption(item.value)"><v-icon>$trash-can-outline</v-icon></v-icon-btn>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <v-row v-if="!isRegistrySchema">
                <!-- Flags -->
                <v-col cols="2" v-if="isAvailableToggleableAttribute('required')">
                    <v-switch
                        label="Required"
                        class="mb-n5 mt-1"
                        v-model="attributeFormField.required"
                    />
                </v-col>
                <v-col cols="2" v-if="isAvailableToggleableAttribute('hidden')">
                    <v-switch
                        label="Hidden"
                        class="mb-n5 mt-1"
                        v-model="attributeFormField.hidden"
                    />
                </v-col>
                <v-col cols="2" v-if="isAvailableToggleableAttribute('editable')">
                    <v-switch
                        label="Editable"
                        class="mb-n5 mt-1"
                        v-model="attributeFormField.editable"
                    />
                </v-col>
                <v-col cols="2" v-if="isAvailableToggleableAttribute('automated')">
                    <v-switch
                        label="Automated"
                        class="mb-n5 mt-1"
                        v-model="attributeFormField.automated"
                    />
                </v-col>
            </v-row>
            <v-row v-if="isNumericWUnits">
                <v-col cols="4">
                    <span>Units</span>
                    <v-text-field
                        label="Add units option"
                        v-model="newUnitsOptionValue"
                        variant="outlined"
                        density="compact"
                        hide-details
                        @change="addUnitsOption"
                    >
                        <template v-slot:append>
                            <v-icon @click="addUnitsOption" color="green"> $plus-circle </v-icon>
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="4">
                    <v-data-table :items="unitsTableItems" :headers="dropdownOptionHeaders" items-per-page="4" density="compact" no-data-text="No units">
                        <template v-slot:item.delete="{ item }">
                            <v-icon-btn @click="removeUnitsOption(item.value)"><v-icon>$trash-can-outline</v-icon></v-icon-btn>
                        </template>
                    </v-data-table>
                </v-col>
                <v-col cols="4">
                    <v-autocomplete
                        label="Default Units"
                        v-model="unitsDefaultValue"
                        class="mb-n5 mt-1"
                        :items="unitsOptions"
                    />
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="cancelEditAttribute"> Cancel </v-btn>
            <v-btn color="primary" @click="saveAttribute">{{ saveButtonText }}</v-btn>
        </v-card-actions>
    </v-card>
</template>
<script setup lang="ts">
import { computed, ref, Ref } from "vue";
import _ from "lodash";
import {
    isEntryRelationAttribute as isEntryRelationAttributeFn,
    isGlobalRelationAttribute as isGlobalRelationAttributeFn,
    isRelationAttribute as isRelationAttributeFn,
} from "@/lib/thingUtils";
import { Attribute, ThingSchema } from "@/services/things/models";
import {
    registriesAPI,
    RegistryEntry,
    RegistrySchema,
    SavedRegistrySchema,
} from "@/services/registries/registriesAPI";
import {
    isRegistrySchema as schemaUtilsIsRegistrySchema,
    isRequestProtocol as schemaUtilsIsRequestProtocol,
    isThingSchema as schemaUtilsIsThingSchema,
} from "@/lib/schemaUtils";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import { withLoadingScreen } from "@/lib/loading";
import { RequestProtocol } from "@/services/samples/samplesAPI";

type Props = {
    attribute: Attribute;
    thingSchema: ThingSchema | RegistrySchema | RequestProtocol;
    isNewAttribute: boolean;
    allRegistrySchemas: SavedRegistrySchema[];
    allThingSchemas?: ThingSchema[];
};

type AttributeFormField = Attribute & {
    // Default to a real value instead of allowing undefined or missing properties on Attribute records.
    editable: boolean;
    required: boolean;
    hidden: boolean;
    automated: boolean;
    widget_type: string;
}

const {
    attribute,
    thingSchema,
    isNewAttribute,
    allRegistrySchemas,
    allThingSchemas,
} = defineProps<Props>();

const emit = defineEmits<{
    (e: "saveAttribute", attribute: Attribute);
    (e: "closeEditAttributeForm");
}>()

const attributeFormField = ref<AttributeFormField>(buildAttributeFormField());
const attributeTypes = ref<string[]>([]);
const widgetTypes = ref<string[]>([]);
const dropdownOptions = ref<string[]>([]);
const registryEntriesForGlobalRelation = ref<RegistryEntry[]>([]);
const unitsOptions = ref<string[]>([]);
const defaultValue = ref(figureOutDefaultValue(attribute));
const unitsDefaultValue = ref(figureOutUnitsDefaultValue(attribute));

function figureOutDefaultValue(attribute: Attribute): string | undefined {
    if (attribute.widget_type === "numeric_w_units") {
        return attribute.value_default || undefined;
    } else {
        return attribute.option_default || undefined;
    }
}

function figureOutUnitsDefaultValue(attribute: Attribute): string | undefined {
    if (attribute.widget_type === "numeric_w_units") {
        return attribute.option_default || undefined;
    } else {
        return undefined;
    }
}

const dropdownOptionHeaders = ref<VDataTableHeader[]>([
    {
        title: "Option",
        value: "value",
        align: "start",
        sortable: true,
        width: "90%",
    },
    {
        title: "Delete",
        value: "delete",
        align: "start",
        sortable: true,
        width: "10%",
    }
]);

const newDropdownOptionValue = ref();
const newUnitsOptionValue = ref();
const isRelation = ref(false);
const saveButtonText = isNewAttribute ? "Add" : "Update";

const isBasicDropdown = computed(() => {
    return isBasicDropdownAttribute(attributeFormField.value);
});

const isBasicDefaultValue = computed(() => {
    return !isBasicDropdown.value && !isRelationAttribute.value;
})

const isNumericWUnits = computed(() => {
    return attributeFormField.value.widget_type === "numeric_w_units";
})

const isRegistrySchema = computed(() => {
    return schemaUtilsIsRegistrySchema(thingSchema);
});

const isGlobalRelationAttribute = computed(() => {
    return isGlobalRelationAttributeFn(attributeFormField.value);
});

const isEntryRelationAttribute = computed(() => {
    return isEntryRelationAttributeFn(attributeFormField.value);
});

const isRelationAttribute = computed(() => {
    return isRelationAttributeFn(attributeFormField.value);
})

const defaultRelationValueOptions = computed(() => {
    if (isGlobalRelationAttribute.value) {
        return registryEntriesForGlobalRelation.value.map((entry) => {
            return {
                title: `${entry.id} - ${entry.displayName}`,
                value: entry.id
            }
        })
    } else if (isEntryRelationAttribute.value) {
        return allThingSchemas!.map((thingSchema) => {
            return {
                title: thingSchema.displayName,
                value: thingSchema.name
            }
        })
    } else {
        return [];
    }
})

const relationOptions = computed(() => {
    if (isGlobalRelationAttribute.value) {
        return allRegistrySchemas;
    } else if (isEntryRelationAttribute.value) {
        return allThingSchemas;
    } else {
        return [];
    }
});

const dropdownTableItems = computed(() => {
    return dropdownOptions.value.map((value) => {
        return {
            value
        }
    })
})

const unitsTableItems = computed(() => {
    return unitsOptions.value.map((value) => {
        return {
            value
        }
    })
})

type AttributeConfiguration = {
    type: string;
    widgetTypes: string[];
    validForSchema: (thingSchema: ThingSchema | RegistrySchema | RequestProtocol) => boolean;
}

type ToggleConfiguration = {
    name: string;
    validForSchema?: (thingSchema: ThingSchema | RegistrySchema | RequestProtocol) => boolean;
}

function validForAllSchema(_: ThingSchema | RegistrySchema | RequestProtocol) {
    return true;
}

function notValidForRequestProtocol(schema: ThingSchema | RegistrySchema | RequestProtocol) {
    return !schemaUtilsIsRequestProtocol(schema);
}

function validForThingSchemaOnly(schema: ThingSchema | RegistrySchema | RequestProtocol) {
    return schemaUtilsIsThingSchema(schema);
}

const availableAttributeConfigurations: AttributeConfiguration[] = [
    {
        type: "date",
        widgetTypes: ["date-picker", "textarea"],
        validForSchema: notValidForRequestProtocol
    },
    {
        type: "datetime",
        widgetTypes: ["datetime-picker", "textarea"],
        validForSchema: notValidForRequestProtocol
    },
    {
        type: "number",
        widgetTypes: ["dropdown", "textarea", "numeric_w_units"],
        validForSchema: validForAllSchema
    },
    {
        type: "string",
        widgetTypes: ["textarea", "dropdown"],
        validForSchema: validForAllSchema
    },
    {
        type: "string_array",
        widgetTypes: ["textarea"],
        validForSchema: notValidForRequestProtocol
    },
    {
        type: "entry_relation",
        widgetTypes: ["dropdown", "textarea"],
        validForSchema: validForThingSchemaOnly
    },
    {
        type: "global_relation",
        widgetTypes: ["dropdown", "textarea"],
        validForSchema: notValidForRequestProtocol
    },
];

const availableToggleableAttributes: ToggleConfiguration[] = [
    {
        name: "required",
        validForSchema: validForAllSchema
    },
    {
        name: "hidden",
        validForSchema: notValidForRequestProtocol
    },
    {
        name: "editable",
        validForSchema: notValidForRequestProtocol
    },
    {
        name: "automated",
        validForSchema: notValidForRequestProtocol
    },
];

function isAvailableToggleableAttribute(attributeName: string): boolean {
    const config = availableToggleableAttributes.find((config) => config.name === attributeName);
    if (!config) {
        return false;
    }
    if (!config.validForSchema) {
        return true;
    }
    return config.validForSchema(thingSchema);
}

async function fetchRegistryEntriesForGlobalRelation() {
    // this method is triggered when the relation is changed, but we share the relation
    // form field with enty_relation attributes.
    if (isGlobalRelationAttribute.value) {
        await withLoadingScreen(async () => {
            const relation = attributeFormField.value.relation!;
            registryEntriesForGlobalRelation.value = await registriesAPI.fetchRegistryEntriesForSchemaName(relation);
        })
    }
}

function isBasicDropdownAttribute(attribute: Attribute) {
    return attribute.widget_type === "dropdown" && !isRelationAttribute.value;
}

function getWidgetTypeConfig(type: string): AttributeConfiguration {
    return availableAttributeConfigurations.find((config) => config.type === type)!;
}

function getAvailableConfigsForSchema(schema: ThingSchema | RegistrySchema | RequestProtocol): AttributeConfiguration[] {
    return availableAttributeConfigurations.filter((config) => {
        return _.isNil(config.validForSchema) || config.validForSchema(schema);
    });
}

function getAttributeTypes(): string[] {
    return getAvailableConfigsForSchema(thingSchema).map((config) => config.type);
}

function buildAttributeFormField(): AttributeFormField {
    return {
        ...attribute,
        // Default to a real value instead of allowing undefined or missing properties on Attribute records.
        editable: _.isNil(attribute.editable) ? true : attribute.editable,
        required: _.isNil(attribute.required) ? false : attribute.required,
        hidden: _.isNil(attribute.hidden) ? false : attribute.hidden,
        automated: _.isNil(attribute.automated) ? false : attribute.automated,
        widget_type: _.isNil(attribute.widget_type) ? "textarea" : attribute.widget_type,
    };
}

function buildFormField() {
    attributeTypes.value = getAttributeTypes();
    attributeFormField.value = buildAttributeFormField();
    dropdownOptions.value = attributeFormField.value.options || [];
    unitsOptions.value = attributeFormField.value.options || [];
    typeChanged();
}

async function typeChanged() {
    const type = attributeFormField.value.type || "textarea";
    const config = getWidgetTypeConfig(type);
    widgetTypes.value = config.widgetTypes;
    isRelation.value = isRelationAttribute.value;
    const currentRelation = attributeFormField.value.relation;
    if (currentRelation) {
        if (isGlobalRelationAttribute.value) {
            if (!isValidGlobalRelation(currentRelation)) {
                attributeFormField.value.relation = undefined;
            }
            await withLoadingScreen(async () => {
                await fetchRegistryEntriesForGlobalRelation();
            })
        } else if (isEntryRelationAttribute.value) {
            if (!isValidEntryRelation(currentRelation)) {
                attributeFormField.value.relation = undefined;
            }
        }
    }
}

function isValidGlobalRelation(relation: string): boolean {
    return !_.isNil(allRegistrySchemas.find((schema) => schema.name === relation));
}

function isValidEntryRelation(relation: string): boolean {
    return !_.isNil(allThingSchemas) && !_.isNil(allThingSchemas.find((schema) => schema.name === relation));
}

function getDropdownOptionType() {
    if (attributeFormField.value.type === "number") {
        return "number";
    } else {
        return "text";
    }
}

function isValidOptionValue(value: string, existingOptions: Ref<string[]>): boolean {
    return !_.isNil(value) && value.trim().length > 0 && !existingOptions.value.includes(value);

}

function addDropdownOption() {
    const value = newDropdownOptionValue.value;
    if (isValidOptionValue(value, dropdownOptions)) {
        dropdownOptions.value.push(value);
        newDropdownOptionValue.value = undefined;
    }
}

function addUnitsOption() {
    const value = newUnitsOptionValue.value;
    if (isValidOptionValue(value, unitsOptions)) {
        unitsOptions.value.push(value);
        newUnitsOptionValue.value = undefined;
    }
}

function removeDropdownOption(optionToRemove: string) {
    dropdownOptions.value = dropdownOptions.value.filter((option) => {
        return option !== optionToRemove;
    });
}

function removeUnitsOption(optionToRemove: string) {
    unitsOptions.value = unitsOptions.value.filter((option) => {
        return option !== optionToRemove;
    });
}

function cancelEditAttribute() {
    emit("closeEditAttributeForm");
}

function saveAttribute() {
    const field = attributeFormField.value;
    const updatedAttribute: Partial<Attribute> = {
        display_name: field.display_name,
        display_order: field.display_order,
        type: field.type,
        widget_type: field.widget_type,
        required: field.required,
        hidden: field.hidden,
        editable: field.editable,
        automated: field.automated,
        allow_multiple_values: field.allow_multiple_values
    };

    if (isNewAttribute) {
        updatedAttribute.name = field.name;
    } else {
        updatedAttribute.name = attribute.name;
    }

    if (field.widget_type === "numeric_w_units") {
        updatedAttribute.options = unitsOptions.value;
        updatedAttribute.value_default = defaultValue.value;
        updatedAttribute.option_default = unitsDefaultValue.value;
    } else {
        if (field.widget_type === "dropdown") {
            updatedAttribute.options = dropdownOptions.value;
        } else {
            updatedAttribute.options = null;
        }
        updatedAttribute.option_default = defaultValue.value;
    }

    if (isRelationAttributeFn(updatedAttribute as Attribute)) {
        updatedAttribute.relation = field.relation;
    } else {
        updatedAttribute.relation = undefined;
    }

    emit("saveAttribute", updatedAttribute as Attribute);
}

buildFormField();

</script>
